import dayjs from 'dayjs';
import 'dayjs/locale/es';
import {
  FirstInstallmentRuleFormValues,
  FirstInstallmentRule,
  ProcessUpdateBody,
} from './types';
import { FirstInstallmentRuleKeys } from './types';
import { SelectOptionType } from '@octano/global-ui';

import { formatDate, toDateOnlyPayload } from '../../../utils/dates';

export const months = Array.from({ length: 12 }, (_, i) => {
  const monthName = dayjs().locale('es').month(i).format('MMMM');
  const monthNumber = dayjs().locale('es').month(i).format('MM');

  return {
    label: monthName.charAt(0).toUpperCase() + monthName.slice(1),
    value: monthNumber,
  };
});

export const years = Array.from({ length: 5 }, (_, i) => {
  const year = dayjs().year() + i;
  return {
    label: year.toString(),
    value: year.toString(),
  };
});

export const getSelectedAdmissionScope = (
  admissionScopes: string[],
  t: (key: string) => string,
) => {
  if (admissionScopes.length === 0) {
    return null;
  }

  if (admissionScopes.length === 2) {
    return {
      value: 'OS,OL',
      label: t('utils.admissionScopes.OS_OL'),
    };
  }

  return admissionScopes.includes('OS')
    ? { value: 'OS', label: t('utils.admissionScopes.OS') }
    : { value: 'OL', label: t('utils.admissionScopes.OL') };
};

export const formatFirstInstallmentRule = (
  data: FirstInstallmentRuleFormValues,
  firstInstallmentRuleId?: number,
): ProcessUpdateBody['firstInstallmentRuleValue'] | null => {
  if (!data.firstInstallmentRule) {
    return null;
  }

  const baseData = data.validityRange
    ? { validity: Number(data.validityRange) }
    : {};

  const ruleConfigs = {
    [FirstInstallmentRuleKeys.SPECIFIC_DATE]: {
      date: data.selectDate ? toDateOnlyPayload(data.selectDate) : undefined,
    },
    [FirstInstallmentRuleKeys.SPECIFIC_MONTH_DAY]: {
      month: `${data.selectYear?.value}-${data.selectMonth?.value}`,
    },
    [FirstInstallmentRuleKeys.NEXT_MONTH]: {},
    [FirstInstallmentRuleKeys.NEXT_MONTH_DAY]: {
      day: data.selectDay ? +data.selectDay : 0,
    },
  };

  const ruleData =
    ruleConfigs[data.firstInstallmentRule.value as keyof typeof ruleConfigs];
  return ruleData
    ? {
        key: formatKey(data.firstInstallmentRule.value.toString()),
        value: { ...baseData, ...ruleData },
        firstInstallmentRuleId,
      }
    : null;
};

const formatKey = (str: string) => {
  return str.replace(/([a-z])([A-Z])/g, '$1_$2').toUpperCase();
};

export const getFirstInstallmentRule = (
  firstInstallmentRule: FirstInstallmentRule,
) => {
  const key =
    FirstInstallmentRuleKeys[
      firstInstallmentRule.code as keyof typeof FirstInstallmentRuleKeys
    ];

  const formValues: {
    selectDay?: string;
    selectMonth?: SelectOptionType;
    selectDate?: string;
    validityRange?: number;
    selectYear?: SelectOptionType;
  } = {};

  const { date, month, day, validity } = firstInstallmentRule.data;

  if (validity) {
    formValues.validityRange = validity;
  }

  if (date) {
    const formatted = formatDate(date, 'YYYY-MM-DD');
    if (formatted) {
      formValues.selectDate = formatted;
    }
  }

  if (month) {
    const [year, monthNumber] = month.split('-');
    formValues.selectYear = { label: year, value: year };
    formValues.selectMonth = months.find((m) => m.value === monthNumber);
  }

  if (day) {
    formValues.selectDay = day.toString();
  }

  return { key, ...formValues };
};
